import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { 
  Home, 
  Briefcase, 
  Users, 
  MessageSquare, 
  User, 
  LogOut,
  CreditCard,
  Bell,
  Star,
  CheckCircle,
  AlertCircle,
  Crown,
  X,
  Shield
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';
import { toast } from 'sonner';

function Navbar() {
  const { user, token, loading, authReady, logout, isBarOwner, isBartender } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const navItems = [
    // Admin Dashboard - only show for admins
    ...(user?.is_admin ? [{
      path: '/admin/dashboard',
      label: 'Admin Dashboard',
      icon: Shield
    }] : []),
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/jobs', label: 'Jobs', icon: Briefcase },
    ...(isBarOwner ? [
      { path: '/owner/browse-bartenders', label: 'Bartenders', icon: Users },
      { path: '/saved-bartenders', label: 'Saved', icon: Star },
    ] : []),
    { path: '/messages', label: 'Messages', icon: MessageSquare },
    { path: '/profile', label: 'Profile', icon: User },
    ...(isBarOwner ? [
      { path: '/account-settings', label: 'Settings', icon: Shield },
    ] : []),
    { path: '/pricing', label: 'Pricing', icon: Crown }
  ];

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [token]);

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get('/notifications/count');
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
    }
  }, [token]);

  useEffect(() => {
    if (loading || !authReady || !user || !token) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    fetchNotifications();
    fetchUnreadCount();

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [user, token, loading, authReady, fetchNotifications, fetchUnreadCount]);

  const markNotificationAsRead = async (notificationId) => {
    try {
      await axios.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'job_posted':
      case 'job_match':
        return <Briefcase className="h-4 w-4 text-primary" />;
      case 'application_received':
      case 'application_accepted':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'application_rejected':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'message_received':
        return <MessageSquare className="h-4 w-4 text-primary" />;
      case 'review_received':
        return <Star className="h-4 w-4 text-[#F5C15D]" />;
      case 'verification_approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'premium_activated':
        return <Star className="h-4 w-4 text-primary" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const formatNotificationTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBackToHome = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="bg-background/70 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center space-x-4">
            <Link to="/dashboard" className="flex items-center space-x-2">
              <div className="text-2xl font-bold text-primary font-['Barlow_Condensed'] flex items-center">
                BARSCONNECT
                {user?.is_premium && (
                  <Star className="h-5 w-5 ml-2 text-primary fill-current" title="Premium User" />
                )}
              </div>
            </Link>
            
            {/* Back to Home Button */}
            <Button
              onClick={handleBackToHome}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-white hover:bg-white/10"
              title="Back to Landing Page"
            >
              <Home className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Home</span>
            </Button>
          </div>

          {/* Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {user?.is_admin && (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="hidden md:flex border-primary/30 text-primary hover:bg-primary/10"
                data-testid="navbar-admin-quick-access-btn"
              >
                <Link to="/admin/dashboard">
                  <Shield className="h-4 w-4 mr-2" />
                  Admin Quick Access
                </Link>
              </Button>
            )}

            {/* Notifications */}
            <Popover open={showNotifications} onOpenChange={setShowNotifications}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="notifications-btn"
                  className="relative hover:bg-white/10"
                >
                  <Bell className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs p-0"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0 bg-background/95 backdrop-blur-xl border-white/10" align="end">
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold font-['Barlow_Condensed'] uppercase">Notifications</h3>
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={markAllAsRead}
                        className="text-xs text-primary hover:text-primary/80"
                      >
                        Mark all read
                      </Button>
                    )}
                  </div>
                </div>
                <ScrollArea className="h-80">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="p-2">
                      {notifications.map((notification) => (
                        <div 
                          key={notification.id}
                          className={`p-3 rounded-lg mb-2 cursor-pointer hover:bg-white/5 transition-colors ${
                            !notification.is_read ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                          }`}
                          onClick={() => !notification.is_read && markNotificationAsRead(notification.id)}
                          data-testid={`notification-${notification.id}`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-1">
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${
                                !notification.is_read ? 'text-white' : 'text-muted-foreground'
                              }`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {notification.message}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatNotificationTime(notification.created_at)}
                              </p>
                            </div>
                            {!notification.is_read && (
                              <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            {/* User Avatar Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage 
                      src={user?.profile_picture} 
                      alt={`${user?.first_name} ${user?.last_name}`} 
                    />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  {user?.is_premium && (
                    <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-0.5">
                      <Star className="h-3 w-3 text-white fill-current" />
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <div className="flex items-center justify-start gap-2 p-2">
                  <div className="flex flex-col space-y-1 leading-none">
                    <p className="font-medium flex items-center">
                      {user?.first_name} {user?.last_name}
                      {user?.is_premium && (
                        <Star className="h-4 w-4 ml-2 text-primary fill-current" />
                      )}
                    </p>
                    <p className="w-[200px] truncate text-sm text-muted-foreground">
                      {user?.email}
                    </p>
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline" className="w-fit">
                        {isBarOwner ? 'Bar Owner' : 'Bartender'}
                      </Badge>
                      {user?.is_premium && (
                        <Badge variant="secondary" className="w-fit bg-primary/10 text-primary">
                          Premium
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link 
                    to="/profile" 
                    data-testid="profile-menu-item"
                    className="flex items-center"
                  >
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/admin/dashboard"
                      data-testid="admin-quick-access-menu-item"
                      className="flex items-center text-primary"
                    >
                      <Shield className="mr-2 h-4 w-4" />
                      Admin Quick Access
                    </Link>
                  </DropdownMenuItem>
                )}
                {isBartender && !user?.is_premium && (
                  <DropdownMenuItem asChild>
                    <Link 
                      to="/premium" 
                      data-testid="upgrade-menu-item"
                      className="flex items-center text-primary"
                    >
                      <Star className="mr-2 h-4 w-4" />
                      Upgrade to Premium
                    </Link>
                  </DropdownMenuItem>
                )}
                {user?.is_premium && (
                  <DropdownMenuItem asChild>
                    <Link 
                      to="/dashboard" 
                      data-testid="analytics-menu-item"
                      className="flex items-center text-primary"
                    >
                      <Star className="mr-2 h-4 w-4" />
                      Analytics Dashboard
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  data-testid="logout-menu-item"
                  onClick={handleLogout}
                  className="text-red-400 focus:text-red-400"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden border-t border-white/10">
        <div className="px-2 pt-2 pb-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={`flex items-center px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                }`}
              >
                <Icon className="h-5 w-5 mr-3" />
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
